import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import { 
  ShieldCheck,
  AlertTriangle,
  LogIn,
  LogOut,
  User,
  Monitor,
  Smartphone,
  Globe,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
  AlertCircle,
  MapPin
} from 'lucide-react'

const ACTION_CONFIG = {
  login_success: { label: 'Login', color: 'green', icon: LogIn },
  login_failed: { label: 'Login Fallito', color: 'red', icon: AlertTriangle },
  logout: { label: 'Logout', color: 'gray', icon: LogOut },
  token_refresh: { label: 'Refresh Token', color: 'blue', icon: ShieldCheck },
  password_reset_request: { label: 'Reset Password Richiesto', color: 'yellow', icon: ShieldCheck },
  password_reset_success: { label: 'Reset Password Completato', color: 'green', icon: ShieldCheck },
  password_change: { label: 'Password Cambiata', color: 'blue', icon: ShieldCheck },
  impersonation_start: { label: 'Impersonificazione Iniziata', color: 'purple', icon: Eye },
  impersonation_end: { label: 'Impersonificazione Terminata', color: 'purple', icon: Eye },
  account_locked: { label: 'Account Bloccato', color: 'red', icon: AlertTriangle },
  session_expired: { label: 'Sessione Scaduta', color: 'orange', icon: LogOut },
  invite_accepted: { label: 'Invito Accettato', color: 'green', icon: User },
  // Azioni 2FA
  '2fa_setup_started': { label: '2FA Setup Iniziato', color: 'blue', icon: ShieldCheck },
  '2fa_setup_completed': { label: '2FA Attivato', color: 'green', icon: ShieldCheck },
  '2fa_verify_success': { label: '2FA Verificato', color: 'green', icon: ShieldCheck },
  '2fa_verify_failed': { label: '2FA Fallito', color: 'red', icon: AlertTriangle },
  '2fa_disabled': { label: '2FA Disattivato', color: 'orange', icon: ShieldCheck },
  '2fa_backup_used': { label: 'Backup Code Usato', color: 'yellow', icon: ShieldCheck },
  passkey_registered: { label: 'Passkey Registrata', color: 'green', icon: ShieldCheck },
  passkey_auth_success: { label: 'Passkey Auth OK', color: 'green', icon: ShieldCheck },
  passkey_auth_failed: { label: 'Passkey Auth Fallita', color: 'red', icon: AlertTriangle }
}

const FAILURE_REASONS = {
  invalid_email: 'Email non trovata',
  invalid_password: 'Password errata',
  account_disabled: 'Account disabilitato',
  account_locked: 'Account bloccato',
  tenant_deleted: 'Centro eliminato',
  tenant_suspended: 'Centro sospeso',
  invalid_token: 'Token non valido',
  expired_token: 'Token scaduto',
  '2fa_required': '2FA richiesto',
  '2fa_invalid': '2FA non valido',
  '2fa_rate_limited': '2FA rate limited'
}

// Helper per estrarre logs dalla risposta API
const extractLogs = (response) => {
  const data = response?.data

  if (data?.success && data?.data?.logs && Array.isArray(data.data.logs)) {
    return { logs: data.data.logs, pagination: data.data.pagination }
  }
  if (data?.success && data?.logs && Array.isArray(data.logs)) {
    return { logs: data.logs, pagination: data.pagination }
  }
  if (Array.isArray(data)) {
    return { logs: data, pagination: null }
  }
  if (Array.isArray(data?.data)) {
    return { logs: data.data, pagination: data.pagination }
  }
  return { logs: [], pagination: null }
}

const extractFailedLogins = (response) => {
  const data = response?.data
  if (data?.success && data?.data?.logs) return data.data.logs
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data)) return data
  return []
}

const extractStats = (response) => {
  const data = response?.data
  if (data?.success && data?.data) return data.data
  if (data?.stats) return data.stats
  if (data?.byAction) return data
  return null
}

const getDeviceIcon = (type) => {
  if (type === 'mobile' || type === 'tablet') {
    return Smartphone
  }
  return Monitor
}

export default function LogsPage() {
  const [logs, setLogs] = useState([])
  const [stats, setStats] = useState(null)
  const [failedLogins, setFailedLogins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('all') // all, failed, impersonations
  const [filters, setFilters] = useState({
    action: '',
    email: '',
    startDate: '',
    endDate: ''
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  })

  useEffect(() => {
    fetchData()
  }, [activeTab, pagination.page, filters])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      if (activeTab === 'all') {
        // Rimuovi parametri vuoti
        const params = {
          page: pagination.page,
          limit: pagination.limit
        }
        if (filters.action) params.action = filters.action
        if (filters.email) params.email = filters.email
        if (filters.startDate) params.startDate = filters.startDate
        if (filters.endDate) params.endDate = filters.endDate
        
        const response = await adminApi.getLogs(params)
        const { logs: extractedLogs, pagination: extractedPagination } = extractLogs(response)
        setLogs(extractedLogs)
        if (extractedPagination) {
          setPagination(prev => ({
            ...prev,
            total: extractedPagination.total || 0,
            pages: extractedPagination.pages || 0
          }))
        }
      } else if (activeTab === 'failed') {
        const response = await adminApi.getFailedLogins({ hours: 720 }) // 30 giorni — allineato alle stats card
        const extracted = extractFailedLogins(response)
        setFailedLogins(extracted)
      } else if (activeTab === 'impersonations') {
        const response = await adminApi.getImpersonationLogs({ limit: 100 })
        const { logs: extractedLogs, pagination: extractedPagination } = extractLogs(response)
        setLogs(extractedLogs)
        if (extractedPagination) {
          setPagination(prev => ({
            ...prev,
            total: extractedPagination.total || 0,
            pages: extractedPagination.pages || 0
          }))
        } else {
          setPagination(prev => ({ ...prev, total: extractedLogs.length, pages: 1 }))
        }
      }

      // Fetch stats (opzionale, non bloccante)
      if (typeof adminApi.getLogsStats === 'function') {
        try {
          const statsResponse = await adminApi.getLogsStats()
          const extractedStats = extractStats(statsResponse)
          setStats(extractedStats)
        } catch (statsErr) {
          console.warn('Errore caricamento stats (non bloccante):', statsErr)
        }
      } else {
        console.warn('adminApi.getLogsStats non disponibile')
      }
    } catch (err) {
      console.error('Errore caricamento logs:', err)
      setError(err.message || 'Errore nel caricamento dei log')
    } finally {
      setLoading(false)
    }
  }

  // FIX: Gestione robusta del timestamp
  const formatDate = (dateValue) => {
    if (!dateValue) return '-'
    
    try {
      const date = new Date(dateValue)
      // Verifica che sia una data valida
      if (isNaN(date.getTime())) {
        return '-'
      }
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (e) {
      return '-'
    }
  }

  // Helper per formattare IP e location
  const formatIpWithLocation = (log) => {
    const ip = log.ipAddress || '-'
    const city = log.location?.city
    const country = log.location?.country
    const source = log.location?.source
    const isApproximate = source === 'ipapi'

    if (city || country) {
      const locationParts = [city, country].filter(Boolean).join(', ')
      return (
        <div>
          <div className="text-gray-900">{ip}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {locationParts}
            {isApproximate && (
              <AlertTriangle
                className="w-3 h-3 text-yellow-600"
                title="Geolocalizzazione approssimativa (provider di fallback)"
                aria-label="Geolocalizzazione approssimativa"
              />
            )}
          </div>
        </div>
      )
    }
    return ip
  }

  const renderActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { label: action, color: 'gray', icon: ShieldCheck }
    const Icon = config.icon
    const colorClasses = {
      green: 'bg-green-100 text-green-700',
      red: 'bg-red-100 text-red-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      orange: 'bg-orange-100 text-orange-700',
      gray: 'bg-gray-100 text-gray-700'
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Log Accessi</h1>
          <p className="text-gray-500 mt-1">Monitora login, logout e attività di sicurezza</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <RefreshCw className="w-4 h-4" />
          Aggiorna
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div>
          <p className="text-xs text-gray-500 mb-2">
            Statistiche {stats.period ? `(${stats.period})` : '(ultimi 30 giorni)'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <LogIn className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Login Riusciti</p>
                  <p className="text-xl font-bold">{stats.byAction?.login_success_total ?? ((stats.byAction?.login_success || 0) + (stats.byAction?.['2fa_verify_success'] || 0))}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Login Falliti</p>
                  <p className="text-xl font-bold">{stats.byAction?.login_failed || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Monitor className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Desktop</p>
                  <p className="text-xl font-bold">{stats.deviceStats?.desktop || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mobile</p>
                  <p className="text-xl font-bold">{stats.deviceStats?.mobile || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {/* Tabs */}
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'all'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Tutti i Log
            </button>
            <button
              onClick={() => setActiveTab('failed')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'failed'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Login Falliti
            </button>
            <button
              onClick={() => setActiveTab('impersonations')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'impersonations'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Impersonificazioni
            </button>
          </nav>
        </div>

        {/* Filters (solo per tab "all") */}
        {activeTab === 'all' && (
          <div className="p-4 border-b bg-gray-50">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <div className="flex-1 min-w-full sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca per email..."
                    value={filters.email}
                    onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full sm:w-auto border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Tutte le azioni</option>
                {Object.entries(ACTION_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="flex-1 sm:flex-none border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="flex-1 sm:flex-none border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={() => setFilters({ action: '', email: '', startDate: '', endDate: '' })}
                className="w-full sm:w-auto px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center gap-2 py-12 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          ) : activeTab === 'failed' ? (
            // Tab Failed Logins
            <div className="space-y-4">
              {failedLogins.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nessun tentativo fallito negli ultimi 7 giorni</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Ora</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP / Località</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {failedLogins.map((log, idx) => (
                        <tr key={log._id || idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(log.timestamp)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {log.email || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-600">
                            {FAILURE_REASONS[log.failureReason] || log.failureReason || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatIpWithLocation(log)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            // Tab All Logs / Impersonations
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Ora</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azione</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispositivo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP / Località</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dettagli</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {logs.map((log) => {
                      const DeviceIcon = getDeviceIcon(log.device?.type)
                      return (
                        <tr key={log._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(log.timestamp)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="min-w-0 max-w-[180px] sm:max-w-none">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {log.userId?.nome && log.userId?.cognome
                                    ? `${log.userId.nome} ${log.userId.cognome}`
                                    : log.email || '-'}
                                </p>
                                {log.userId?.email && log.userId.email !== log.email && (
                                  <p className="text-xs text-gray-500 truncate">{log.userId.email}</p>
                                )}
                                {log.tenantId?.name && (
                                  <p className="text-xs text-gray-400 truncate">{log.tenantId.name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {renderActionBadge(log.action)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <DeviceIcon className="w-4 h-4" />
                              <span>{log.device?.browser || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {formatIpWithLocation(log)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {log.failureReason && (
                              <span className="text-red-600">
                                {FAILURE_REASONS[log.failureReason] || log.failureReason}
                              </span>
                            )}
                            {log.impersonatedBy && (
                              <span className="text-purple-600">
                                Da: {log.impersonatedBy.nome || log.impersonatedBy.email}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination (solo per tab "all") */}
              {activeTab === 'all' && pagination.pages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t pt-4">
                  <p className="text-sm text-gray-500">
                    Pagina {pagination.page} di {pagination.pages} ({pagination.total} totali)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      disabled={pagination.page >= pagination.pages}
                      className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {logs.length === 0 && (
                <p className="text-center text-gray-500 py-8">Nessun log trovato</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
